// ChatWidget.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, ArrowLeft, Search, X, Check, CheckCircle, MessageSquare, Filter } from 'react-feather';
import authAxios from "../../services/Token";
import Swal from 'sweetalert2';
import './ChatWidget.css';

const ChatWidget = ({ isOpen, onClose }) => {
  // Estados principales
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [totalUnread, setTotalUnread] = useState(0);
  
  // Nuevo estado para el filtro
  const [filterType, setFilterType] = useState('todos'); // 'todos', 'no_leidos', 'leidos'

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const pollingInterval = useRef(null);
  const isMounted = useRef(true);

  // Obtener ID del usuario actual
  const getCurrentUserId = useCallback(() => {
    try {
      return parseInt(localStorage.getItem('idusuario') || sessionStorage.getItem('idusuario'));
    } catch {
      return null;
    }
  }, []);

  // Generar avatar por defecto
  const getDefaultAvatar = (name, size = 48) => {
    const initial = name ? name.charAt(0).toUpperCase() : '?';
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E%3Ccircle cx='${size/2}' cy='${size/2}' r='${size/2}' fill='%234CAF50'/%3E%3Ctext x='${size/2}' y='${size/2+5}' font-size='${size/2.5}' text-anchor='middle' fill='%23ffffff' font-family='Arial' dominant-baseline='middle'%3E${initial}%3C/text%3E%3C/svg%3E`;
  };

  // Scroll al final
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
    }
  }, []);

  // Scroll inmediato al final
  const scrollToBottomInstant = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ block: 'end' });
    }
  }, []);

  // Cargar datos iniciales cuando se abre el chat
  useEffect(() => {
    if (!isOpen) return;

    loadInitialData();

    const interval = setInterval(() => {
      refreshChatData();
    }, 2000);

    return () => clearInterval(interval);
  }, [isOpen, selectedUser]);

  // Filtrar usuarios por búsqueda y tipo de filtro
  useEffect(() => {
    let filtered = [...users];
    
    // Aplicar filtro por tipo
    if (filterType === 'no_leidos') {
      filtered = filtered.filter(user => user.no_leidos > 0);
    } else if (filterType === 'leidos') {
      filtered = filtered.filter(user => user.no_leidos === 0);
    }
    
    // Aplicar filtro por búsqueda
    if (searchTerm.trim()) {
      filtered = filtered.filter(user => 
        user.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.nombre_corto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.cargo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredUsers(filtered);
  }, [searchTerm, users, filterType]);

  // Scroll al final cuando cambian los mensajes
  useEffect(() => {
    if (selectedUser && messages.length > 0) {
      setTimeout(() => {
        scrollToBottomInstant();
      }, 100);
    }
  }, [messages, selectedUser]);

  // Refrescar datos del chat
  const refreshChatData = async () => {
    try {
      console.log('🔄 Ejecutando refreshChatData...');
      
      // 1. Siempre actualizar contadores de no leídos
      await loadUnreadCounts();
      console.log('✅ loadUnreadCounts completado');
      
      // 2. Siempre actualizar lista de usuarios
      await loadUsers();
      console.log('✅ loadUsers completado');
      
      // 3. SIEMPRE refrescar mensajes si hay usuario seleccionado
      if (selectedUser) {
        console.log(`💬 Refrescando mensajes con ${selectedUser.nombre_corto} (ID: ${selectedUser.id})`);
        await refreshMessages(selectedUser.id);
      } else {
        console.log('ℹ️ No hay usuario seleccionado, no se refrescan mensajes');
      }
    } catch (error) {
      console.error('Error en refreshChatData:', error);
    }
  };

  // Refrescar mensajes
  const refreshMessages = async (userId) => {
    try {
      const response = await authAxios.get(`/chat/messages/${userId}`);

      if (response.data?.success) {
        const newMessages = response.data.data;
        setMessages(newMessages);
      }
    } catch (error) {
      console.error('Error en refreshMessages:', error);
    }
  };

  // Cargar datos iniciales
  const loadInitialData = async () => {
    await Promise.all([
      loadUsers(),
      loadUnreadCounts()
    ]);
  };

  // Cargar usuarios
  const loadUsers = async () => {
    try {
      const response = await authAxios.get('/chat/users');
      if (response.data?.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // Cargar contadores
  const loadUnreadCounts = async () => {
    try {
      const response = await authAxios.get('/chat/unread');
      if (response.data?.success) {
        setTotalUnread(response.data.data.total);
        setUnreadCounts(response.data.data.por_usuario);
      }
    } catch (error) {
      console.error('Error loading unread counts:', error);
    }
  };

  // Cargar mensajes
  const loadMessages = async (userId) => {
    setLoading(true);
    
    try {
      console.log(`📥 Cargando mensajes iniciales de /chat/messages/${userId}`);
      const response = await authAxios.get(`/chat/messages/${userId}`);
      
      if (response.data?.success) {
        const loadedMessages = response.data.data;
        console.log(`📥 Cargados ${loadedMessages.length} mensajes`);
        setMessages(loadedMessages);
        
        // Marcar como leídos
        await markMessagesAsRead(userId);
        
        setTimeout(() => {
          scrollToBottomInstant();
        }, 200);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Marcar mensajes como leídos
  const markMessagesAsRead = async (userId) => {
    try {
      await authAxios.post('/chat/messages/mark-read', { usuario_id: userId });
      setUnreadCounts(prev => ({ ...prev, [userId]: 0 }));
      setTotalUnread(prev => Math.max(0, prev - (unreadCounts[userId] || 0)));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Enviar mensaje
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedUser || sendingMessage) return;

    const currentUserId = getCurrentUserId();
    const messageText = newMessage.trim();
    
    setSendingMessage(true);
    setNewMessage('');

    // Mensaje temporal para feedback inmediato
    const tempMessage = {
      id: `temp-${Date.now()}`,
      mensaje: messageText,
      fecha: new Date().toISOString(),
      es_mio: true,
      usuario_id: currentUserId,
      receptor_id: selectedUser.id,
      estado: 0,
      usuario: { 
        nombre: 'Tú', 
        nombre_corto: 'Tú' 
      }
    };

    setMessages(prev => [...prev, tempMessage]);
    scrollToBottomInstant();

    try {
      console.log('📤 Enviando mensaje...');
      const response = await authAxios.post('/chat/messages/send', {
        receptor_id: selectedUser.id,
        mensaje: messageText
      });

      if (response.data?.success) {
        console.log('✅ Mensaje enviado correctamente');
        
        // Recargar mensajes para asegurar consistencia
        const messagesResponse = await authAxios.get(`/chat/messages/${selectedUser.id}`);
        if (messagesResponse.data?.success) {
          setMessages(messagesResponse.data.data);
          scrollToBottomInstant();
        }
        
        // Actualizar lista de usuarios
        await loadUsers();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Eliminar mensaje temporal
      setMessages(prev => prev.filter(msg => !msg.id.toString().startsWith('temp-')));
      Swal.fire({ 
        icon: 'error', 
        title: 'Error', 
        text: 'No se pudo enviar el mensaje', 
        timer: 2000 
      });
    } finally {
      setSendingMessage(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Abrir chat
  const openChat = async (user) => {
    console.log(`👆 Abriendo chat con ${user.nombre_corto} (ID: ${user.id})`);
    setSelectedUser(user);
    setMessages([]);
    
    await loadMessages(user.id);
    
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };

  // Cerrar chat
  const closeChat = () => {
    console.log('👈 Volviendo a lista de usuarios');
    setSelectedUser(null);
    setMessages([]);
    setSearchTerm('');
  };

  // Formatear hora
// ChatWidget.jsx - Función formatTime CORREGIDA

const formatTime = (dateString) => {
  try {
    // Verificar si la fecha viene en formato MySQL (con espacio) o ISO (con T)
    if (dateString.includes(' ')) {
      // Formato MySQL: "2026-03-04 14:28:18"
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);
      
      // Crear fecha directamente con los componentes (sin conversión de zona)
      // IMPORTANTE: month - 1 porque JavaScript meses son 0-11
      const colombiaDate = new Date(year, month - 1, day, hour, minute, 0);
      
      const now = new Date();
      const isToday = colombiaDate.toDateString() === now.toDateString();
      
      if (isToday) {
        return colombiaDate.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false
        });
      }
      
      return colombiaDate.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit',
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      });
    } else {
      // Formato ISO: "2026-03-04T14:28:18.000Z"
      const date = new Date(dateString);
      
      // Ajustar manualmente a Colombia (UTC-5)
      const colombiaDate = new Date(date.getTime() - (5 * 60 * 60 * 1000));
      
      const now = new Date();
      const isToday = colombiaDate.toDateString() === now.toDateString();
      
      if (isToday) {
        return colombiaDate.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false
        });
      }
      
      return colombiaDate.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit',
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      });
    }
  } catch (error) {
    console.error('Error formateando fecha:', error, dateString);
    return '';
  }
};

  // Obtener contador de no leídos para el filtro
  const getUnreadCount = () => {
    return users.filter(user => user.no_leidos > 0).length;
  };

  const getReadCount = () => {
    return users.filter(user => user.no_leidos === 0).length;
  };

  if (!isOpen) return null;

  return (
    <div className="chat-widget open">
      {/* Header */}
      <div className="chat-header">
        <div className="chat-header-left">
          {selectedUser ? (
            <button onClick={closeChat} className="back-button">
              <ArrowLeft size={18} />
            </button>
          ) : (
            <MessageSquare size={20} />
          )}
          <h3>{selectedUser ? selectedUser.nombre_corto || selectedUser.nombre : 'Mensajes'}</h3>
        </div>
        <button onClick={onClose} className="close-button">
          <X size={18} />
        </button>
      </div>

      {/* Contenido */}
      <div className="chat-content">
        {!selectedUser ? (
          /* Lista de usuarios */
          <div className="users-list">
            <div className="search-box">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar usuario..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="clear-search">
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Filtros */}
            <div className="filter-tabs">
              <button 
                className={`filter-tab ${filterType === 'todos' ? 'active' : ''}`}
                onClick={() => setFilterType('todos')}
              >
                Todos ({users.length})
              </button>
              <button 
                className={`filter-tab ${filterType === 'no_leidos' ? 'active' : ''}`}
                onClick={() => setFilterType('no_leidos')}
              >
                No leídos ({getUnreadCount()})
              </button>
              <button 
                className={`filter-tab ${filterType === 'leidos' ? 'active' : ''}`}
                onClick={() => setFilterType('leidos')}
              >
                Leídos ({getReadCount()})
              </button>
            </div>

            <div className="users-scroll">
              {users.length === 0 ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Cargando usuarios...</p>
                </div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <div
                    key={user.id}
                    className={`user-item ${user.no_leidos ? 'has-unread' : ''}`}
                    onClick={() => openChat(user)}
                  >
                    <div className="user-avatar">
                      <img 
                        src={user.avatar || getDefaultAvatar(user.nombre, 48)}
                        alt={user.nombre}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = getDefaultAvatar(user.nombre, 48);
                        }}
                      />
                    </div>
                    <div className="user-info">
                      <div className="user-header">
                        <h4>{user.nombre_corto || user.nombre}</h4>
                        {user.no_leidos > 0 && (
                          <span className="unread-badge">{user.no_leidos}</span>
                        )}
                      </div>
                      <p className="user-role">{user.cargo || 'Sin cargo'}</p>
                      {user.ultimo_mensaje && (
                        <div className="last-message">
                          <span className="message-preview">
                            {user.ultimo_mensaje.es_mio && 'Tú: '}
                            {user.ultimo_mensaje.texto}
                            {user.ultimo_mensaje.texto?.length >= 50 ? '...' : ''}
                          </span>
                          <span className="message-time">
                            {formatTime(user.ultimo_mensaje.fecha)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <MessageSquare size={40} />
                  <p>
                    {filterType === 'todos' && (searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios disponibles')}
                    {filterType === 'no_leidos' && 'No hay mensajes no leídos'}
                    {filterType === 'leidos' && 'No hay mensajes leídos'}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Chat con usuario */
          <div className="chat-area">
            <div className="chat-user-header">
              <div className="user-info">
                <div className="user-avatar small">
                  <img 
                    src={selectedUser.avatar || getDefaultAvatar(selectedUser.nombre, 40)}
                    alt={selectedUser.nombre}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getDefaultAvatar(selectedUser.nombre, 40);
                    }}
                  />
                </div>
                <div>
                  <h4>{selectedUser.nombre_corto || selectedUser.nombre}</h4>
                  <p className="user-role">{selectedUser.cargo || 'Sin cargo'}</p>
                </div>
              </div>
            </div>

            <div className="messages-container" ref={messagesContainerRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Cargando mensajes...</p>
                </div>
              ) : messages.length > 0 ? (
                messages.map((message, index) => {
                  const showDate = index === 0 || 
                    new Date(message.fecha).toDateString() !== new Date(messages[index - 1]?.fecha).toDateString();
                  
                  return (
                    <React.Fragment key={message.id}>
                      {showDate && (
                        <div className="date-divider">
                          <span>
                            {new Date(message.fecha).toLocaleDateString('es-ES', { 
                              weekday: 'long', 
                              day: 'numeric', 
                              month: 'long' 
                            })}
                          </span>
                        </div>
                      )}
                      <div className={`message ${message.es_mio ? 'sent' : 'received'}`}>
                        <div className="message-content">
                          <p>{message.mensaje}</p>
                          <div className="message-footer">
                            <span className="message-time">{formatTime(message.fecha)}</span>
                            {message.es_mio && (
                              <span className="message-status">
                                {message.estado === 1 ? <CheckCircle size={14} /> : <Check size={14} />}
                              </span>
                            )}
                            {!message.es_mio && message.estado === 0 && (
                              <span className="message-status unread">Nuevo</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              ) : (
                <div className="empty-state">
                  <MessageSquare size={40} />
                  <p>No hay mensajes aún</p>
                  <p className="small">Envía el primer mensaje</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="message-input">
              <input
                ref={inputRef}
                type="text"
                placeholder="Escribe un mensaje..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !sendingMessage && newMessage.trim()) {
                    sendMessage();
                  }
                }}
                disabled={sendingMessage}
              />
              <button 
                onClick={sendMessage} 
                disabled={!newMessage.trim() || sendingMessage}
                className={newMessage.trim() ? 'active' : ''}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;